/**
 * Un plan d'achat, et la seule question qui compte avant de l'approuver :
 * **est-il toujours basé sur les mêmes données ?** (V1.6-08)
 *
 * C'est pour cette question que la bannière de péremption passe avant tout le
 * reste — avant les totaux, avant les lignes. Un plan qui a l'air propre mais
 * repose sur un prix d'avant-hier est plus dangereux qu'un plan visiblement
 * incomplet : le premier se signe sans hésiter.
 *
 * Ce que cet écran **ne fait pas**, et qui n'a donc aucun bouton : créer un bon
 * de commande, envoyer la décision à un fournisseur, réserver du stock,
 * recevoir, payer. Approuver fige une décision ; ça n'exécute rien.
 *
 * Et ce qu'il ne calcule pas :
 *
 * * **la péremption.** Elle vient du serveur, qui compare l'empreinte figée à
 *   celle d'aujourd'hui. L'écran affiche `staleness`, il ne le déduit pas ;
 * * **les totaux.** Aucun `unit_price × quantité` ici. Les montants affichés
 *   sont ceux que le serveur a figés, et l'approbation les recalcule de son
 *   côté sans jamais lire ce que le navigateur envoie ;
 * * **le droit d'approuver.** `can_approve` est une réponse, pas une opinion.
 *   Masquer le bouton est une politesse ; le serveur refuse de toute façon.
 */
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, RefreshCw, ShieldCheck, Truck, XCircle } from "lucide-react";

import { useCreateOrdersFromPlan, usePlanPurchaseOrders } from "@/api/purchase-orders";
import {
  useApprovePurchasePlan,
  useCancelPurchasePlan,
  usePurchasePlan,
  useRefreshPurchasePlan,
  useUpdatePurchasePlan,
} from "@/api/purchase-plans";
import type {
  ComparisonWarning,
  PlanStaleness,
  PurchasePlanDetail,
  PurchasePlanLine,
  PurchasePlanQuote,
} from "@/api/types";
import { useAuth } from "@/auth/useAuth";
import { Button } from "@/components/Button";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { ComparableDecisionsCard } from "./ComparableDecisionsCard";
import { TableShell, Td, Th, Tr } from "@/components/DataTable";
import { Dialog } from "@/components/Dialog";
import { Field, Input } from "@/components/Input";
import { ErrorState, LoadingState } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/toast-context";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import {
  coverageTone,
  formatAmount,
  formatAssertedAvailability,
  formatComparisonWarning,
  formatCoverage,
  formatLeadTime,
  formatScenarioKind,
  warningTone,
} from "@/lib/comparison-display";
import { formatDate, formatDateTime, formatPurchaseOrderStatus, formatQuantityWithUnit, pluralize } from "@/lib/format";
import { reportMutationError } from "@/lib/mutation-errors";
import {
  formatAcknowledgement,
  formatPlanChange,
  formatPlanCode,
  formatPlanSource,
  formatPlanStatus,
  formatPlanTotal,
  planStatusTone,
  planTotals,
  whyCannotApprove,
} from "@/lib/plan-display";

export function PurchasePlanDetailPage() {
  const { planId } = useParams<{ planId: string }>();
  const query = usePurchasePlan(planId);

  if (query.isLoading) return <LoadingState label="Lecture du plan…" />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  if (!query.data) return null;

  return <PlanView detail={query.data} />;
}

function PlanView({ detail }: { detail: PurchasePlanDetail }) {
  const { plan, staleness } = detail;
  const toast = useToast();
  const { can } = useAuth();
  const refresh = useRefreshPurchasePlan();
  const cancel = useCancelPurchasePlan();
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState("");

  // Les rôles du serveur, redits ici pour ne pas montrer un bouton qui serait
  // refusé. Ce n'est pas une frontière de sécurité — celle-là est côté API.
  const mayApprove = can("ADMIN", "MANAGER");
  const mayPrepare = can("ADMIN", "MANAGER", "TECHNICIAN");
  const editable = plan.status === "DRAFT";

  async function doRefresh() {
    try {
      const next = await refresh.mutateAsync({ planId: plan.id });
      toast.success(
        "Plan reconstruit",
        next.plan.id === plan.id
          ? "Le brouillon repose maintenant sur les données d'aujourd'hui."
          : `Le plan approuvé n'a pas bougé. Nouvelle révision : ${formatPlanCode(next.plan)}.`,
      );
    } catch (error) {
      reportMutationError(toast, "Plan non reconstruit", error);
    }
  }

  async function doCancel() {
    try {
      await cancel.mutateAsync({ planId: plan.id, reason: reason.trim() });
      toast.success("Plan annulé", "Il reste lisible, avec sa raison.");
      setCancelling(false);
      setReason("");
    } catch (error) {
      reportMutationError(toast, "Plan non annulé", error);
    }
  }

  return (
    <Page>
      <PageHeader
        crumbs={[
          { label: "Plans d'achat", to: "/purchase-plans" },
          { label: formatPlanCode(plan) },
        ]}
        title={formatPlanCode(plan)}
        subtitle={
          <>
            {plan.rfq_code ? (
              <Link
                to={`/rfqs/${plan.rfq_id}/comparison`}
                className="text-info underline underline-offset-2"
              >
                {plan.rfq_code}
              </Link>
            ) : (
              "Demande retirée"
            )}
            {" · "}
            {formatPlanSource(plan.source)}
            {plan.source_scenario_kind
              ? ` (${formatScenarioKind(plan.source_scenario_kind)})`
              : ""}
            {" · figé sur la comparaison du "}
            {formatDate(plan.comparison_computed_at)}
          </>
        }
        actions={
          <>
            <StatusBadge tone={planStatusTone(plan.status)}>
              {formatPlanStatus(plan.status)}
            </StatusBadge>
            <StatusBadge tone={coverageTone(plan.coverage)}>
              {formatCoverage(plan.coverage)}
            </StatusBadge>
            {mayPrepare && plan.status !== "CANCELLED" && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void doRefresh()}
                disabled={refresh.isPending}
              >
                <RefreshCw className="size-4" aria-hidden />
                {refresh.isPending ? "Reconstruction…" : "Rafraîchir"}
              </Button>
            )}
            {mayPrepare && editable && (
              <Button variant="ghost" size="sm" onClick={() => setCancelling(true)}>
                <XCircle className="size-4" aria-hidden />
                Annuler
              </Button>
            )}
          </>
        }
      />

      <div className="space-y-4">
        <StaleBanner staleness={staleness} />
        <Totals detail={detail} />
        <Approval detail={detail} mayApprove={mayApprove} />
          <OrdersFromPlan detail={detail} />
        <Lines detail={detail} editable={editable && mayPrepare} />
        <Quotes quotes={detail.quotes} />
        <ComparableDecisionsCard detail={detail} />
        <Revisions detail={detail} />
      </div>

      <Dialog
        open={cancelling}
        onOpenChange={(next) => {
          if (!next) setReason("");
          setCancelling(next);
        }}
        title="Annuler ce plan"
        description="Le plan reste lisible avec sa raison. Rien n'est effacé."
        footer={
          <>
            <Button variant="secondary" onClick={() => setCancelling(false)}>
              Revenir
            </Button>
            <Button
              variant="danger"
              onClick={() => void doCancel()}
              disabled={!reason.trim() || cancel.isPending}
            >
              {cancel.isPending ? "Annulation…" : "Annuler le plan"}
            </Button>
          </>
        }
      >
        <Field label="Raison" hint="Obligatoire. Elle sera lue par quelqu'un d'autre.">
          <Input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Le chantier est reporté…"
            aria-label="Raison de l'annulation"
          />
        </Field>
      </Dialog>
    </Page>
  );
}

/**
 * « Ce plan repose-t-il encore sur les mêmes données ? »
 *
 * En haut, avant tout le reste, et rouge quand la réponse est non. Chaque écart
 * est nommé avec son avant et son après : « le prix a changé » ne permet aucune
 * décision, « il est passé de 12,50 à 13,75 » en permet une.
 *
 * Quand l'empreinte a bougé sans qu'aucun écart ne soit nommable, on le dit
 * plutôt que d'inventer une cause.
 */
function StaleBanner({ staleness }: { staleness: PlanStaleness }) {
  if (!staleness.stale) {
    return (
      <div
        className="flex items-start gap-2 rounded-lg border border-status-available/30 bg-success-subtle px-4 py-3"
        data-testid="plan-fresh"
      >
        <ShieldCheck className="mt-0.5 size-4 shrink-0 text-status-available" aria-hidden />
        <p className="text-[13px] text-ink">
          Ce plan repose toujours sur les mêmes données qu'au moment où il a été figé.
        </p>
      </div>
    );
  }

  return (
    <div
      className="space-y-2 rounded-lg border border-status-out/40 bg-danger-subtle px-4 py-3"
      data-testid="plan-stale"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 size-4 shrink-0 text-status-out" aria-hidden />
        <div className="min-w-0">
          <p className="text-[13px] font-medium text-ink">
            Les données ont changé depuis que ce plan a été figé.
          </p>
          <p className="text-[12px] text-ink-muted">
            L'approbation est refusée tant qu'il n'a pas été reconstruit et relu.
          </p>
        </div>
      </div>

      {staleness.changes.length > 0 ? (
        <ul className="space-y-1 pl-6 text-[12px] text-ink">
          {staleness.changes.map((change, index) => (
            <li key={`${change.kind}-${change.plan_line_id ?? change.quote_id ?? index}`}>
              {formatPlanChange(change)}
            </li>
          ))}
        </ul>
      ) : null}

      {!staleness.explained && (
        <p className="pl-6 text-[12px] text-ink-muted">
          Une partie du changement n'a pas pu être nommée. Comparez avec la comparaison courante
          avant de reconstruire.
        </p>
      )}
    </div>
  );
}

/** Les montants figés — une entrée par devise, jamais une somme. */
function Totals({ detail }: { detail: PurchasePlanDetail }) {
  const { plan } = detail;
  const totals = planTotals(plan);

  return (
    <Card>
      <CardHeader
        title="Ce que ce plan engagerait"
        subtitle="Les montants figés au moment de la décision, pas ceux d'aujourd'hui."
      />
      <CardBody className="space-y-3">
        {totals.length === 0 ? (
          <p className="text-[13px] text-ink-subtle">Aucune ligne retenue.</p>
        ) : (
          <ul className="space-y-1">
            {totals.map(([currency, amounts]) => (
              <li key={currency} className="text-[18px] font-semibold text-ink">
                {formatPlanTotal(amounts, currency)}
              </li>
            ))}
          </ul>
        )}

        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[12px] sm:grid-cols-4">
          <dt className="text-ink-subtle">Lignes</dt>
          <dd className="text-ink">{plan.line_count}</dd>
          <dt className="text-ink-subtle">Fournisseurs</dt>
          <dd className="text-ink">
            {plan.supplier_count}
            {plan.quote_count > plan.supplier_count && (
              <span className="text-ink-subtle">
                {" "}
                ({pluralize(plan.quote_count, "soumission", "soumissions")})
              </span>
            )}
          </dd>
          <dt className="text-ink-subtle">Délai</dt>
          <dd className="text-ink">{formatLeadTime(plan.lead_time_days)}</dd>
          <dt className="text-ink-subtle">Créé</dt>
          <dd className="text-ink">
            {plan.created_by} · {formatDateTime(plan.created_at)}
          </dd>
        </dl>

        {plan.notes && <p className="text-[13px] text-ink-muted">{plan.notes}</p>}
        {plan.cancellation_reason && (
          <p className="text-[13px] text-ink-muted">
            Annulé par {plan.cancelled_by} : {plan.cancellation_reason}
          </p>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * Approuver, en nommant un par un les avertissements acceptés.
 *
 * Il n'y a pas de case « je force ». Chaque avertissement acceptable se coche
 * séparément, parce qu'un drapeau global dirait « j'accepte ce que je n'ai pas
 * lu » — et parce que ce qui a été accepté doit rester lisible six mois plus
 * tard, avec le nom de qui l'a accepté.
 *
 * Ce que rien ne coche : les avertissements bloquants. Aucune case ne les lève.
 */
function Approval({
  detail,
  mayApprove,
}: {
  detail: PurchasePlanDetail;
  mayApprove: boolean;
}) {
  const toast = useToast();
  const approve = useApprovePurchasePlan();
  const [accepted, setAccepted] = useState<string[]>([]);

  const required = detail.required_acknowledgements;
  const missing = useMemo(
    () => required.filter((item) => !accepted.includes(item)),
    [required, accepted],
  );
  const refusal = whyCannotApprove(detail);

  if (detail.plan.status === "APPROVED") {
    return (
      <Card>
        <CardHeader
          title="Décision approuvée"
          subtitle={`${detail.plan.approved_by} · ${formatDateTime(detail.plan.approved_at)}`}
          action={<StatusBadge tone="available">Approuvé</StatusBadge>}
        />
        <CardBody className="space-y-2">
          <p className="text-[13px] text-ink-muted">
            Approuver a figé une décision, ça ne l'a pas exécutée : aucune commande n'a été créée
            et aucun fournisseur n'a été prévenu. Créer les bons est le geste suivant, et il se
            fait ci-dessous.
          </p>
          {detail.plan.acknowledgements.length > 0 && (
            <ul className="space-y-1 text-[12px] text-ink">
              {detail.plan.acknowledgements.map((item) => (
                <li key={item.warning}>
                  {formatAcknowledgement(item.warning)} — {item.acknowledged_by},{" "}
                  {formatDateTime(item.acknowledged_at)}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    );
  }

  async function submit() {
    try {
      await approve.mutateAsync({ planId: detail.plan.id, acknowledgements: accepted });
      toast.success("Plan approuvé", "La décision est figée. Rien n'a été commandé.");
      setAccepted([]);
    } catch (error) {
      reportMutationError(toast, "Plan non approuvé", error);
    }
  }

  return (
    <Card>
      <CardHeader
        title="Approuver"
        subtitle="Fige la décision. Ne crée aucune commande et ne prévient aucun fournisseur."
      />
      <CardBody className="space-y-3">
        {detail.blocking_warnings.length > 0 && (
          <div className="space-y-1">
            <p className="text-[13px] font-medium text-ink">Ce qu'aucune acceptation ne lève :</p>
            <ul className="space-y-1">
              {detail.blocking_warnings.map((warning) => (
                <li key={warning}>
                  <StatusBadge tone="out">
                    {formatComparisonWarning(warning as ComparisonWarning)}
                  </StatusBadge>
                </li>
              ))}
            </ul>
          </div>
        )}

        {required.length > 0 && (
          <fieldset className="space-y-2">
            <legend className="text-[13px] font-medium text-ink">
              À accepter explicitement, un par un
            </legend>
            {required.map((warning) => (
              <label key={warning} className="flex items-start gap-2 text-[13px] text-ink">
                <input
                  type="checkbox"
                  className="mt-0.5 size-4"
                  checked={accepted.includes(warning)}
                  onChange={(event) =>
                    setAccepted((current) =>
                      event.target.checked
                        ? [...current, warning]
                        : current.filter((item) => item !== warning),
                    )
                  }
                />
                <span>{formatAcknowledgement(warning)}</span>
              </label>
            ))}
          </fieldset>
        )}

        {refusal && <p className="text-[13px] text-ink-muted">{refusal}</p>}
        {!mayApprove && (
          <p className="text-[13px] text-ink-muted">
            Votre rôle ne permet pas d'approuver un plan d'achat.
          </p>
        )}

        <Button
          variant="primary"
          size="sm"
          onClick={() => void submit()}
          disabled={
            !mayApprove || !detail.can_approve || missing.length > 0 || approve.isPending
          }
        >
          <ShieldCheck className="size-4" aria-hidden />
          {approve.isPending ? "Approbation…" : "Approuver ce plan"}
        </Button>
        {missing.length > 0 && detail.can_approve && (
          <p className="text-[12px] text-ink-subtle">
            {pluralize(missing.length, "acceptation manquante", "acceptations manquantes")}.
          </p>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * Créer les bons de commande de ce plan. **Le geste d'après l'approbation.**
 *
 * Un bon par soumission retenue, jamais un par fournisseur : le transport
 * appartient à une soumission, et deux soumissions validées du même fournisseur
 * en portent deux. Les fusionner obligerait à additionner deux transports ou à
 * en perdre un.
 *
 * Rappelé, ce bouton ne crée rien de plus : l'idempotence tient à un unique en
 * base, pas à la prudence de l'écran. Et si le plan n'est plus frais, le serveur
 * refuse avec `STALE_APPROVED_PLAN` — la réponse est de créer une révision et de
 * la faire approuver, pas de passer outre. Il n'y a pas de « forcer ».
 */
function OrdersFromPlan({ detail }: { detail: PurchasePlanDetail }) {
  const toast = useToast();
  const create = useCreateOrdersFromPlan();
  const { data: orders, isLoading } = usePlanPurchaseOrders(detail.plan.id);

  if (detail.plan.status !== "APPROVED") return null;

  async function submit() {
    try {
      const made = await create.mutateAsync({ planId: detail.plan.id });
      toast.success(
        pluralize(made.total, "commande créée", "commandes créées"),
        "Créées, pas passées : marquez-les commandées quand vous les aurez envoyées.",
      );
    } catch (error) {
      reportMutationError(toast, "Commandes non créées", error);
    }
  }

  const existing = orders?.items ?? [];

  return (
    <Card>
      <CardHeader
        title="Commandes"
        subtitle="Un bon par soumission retenue. Créer un bon ne le passe pas chez le fournisseur."
      />
      <CardBody className="space-y-2">
        {existing.length > 0 && (
          <ul className="space-y-1 text-[13px]">
            {existing.map((order) => (
              <li key={order.id}>
                <Link to={`/commandes/${order.id}`} className="text-brand hover:underline">
                  {order.code}
                </Link>{" "}
                <span className="text-ink-muted">
                  · {order.supplier_name} · {formatPurchaseOrderStatus(order.status)}
                </span>
              </li>
            ))}
          </ul>
        )}

        {existing.length === 0 && !isLoading && (
          <p className="text-[13px] text-ink-muted">
            Aucune commande n'existe encore pour ce plan.
          </p>
        )}

        <Button
          variant={existing.length === 0 ? "primary" : "secondary"}
          size="sm"
          onClick={() => void submit()}
          disabled={create.isPending}
        >
          <Truck className="size-4" aria-hidden />
          {create.isPending
            ? "Création…"
            : existing.length === 0
              ? "Créer les commandes"
              : "Créer les commandes manquantes"}
        </Button>
        <p className="text-[12px] text-ink-subtle">
          Rappeler ce bouton ne crée pas de doublon. Si le plan n'est plus frais, la création est
          refusée : créez une révision et faites-la approuver.
        </p>
      </CardBody>
    </Card>
  );
}

/**
 * Ajuster ce qu'on prend sur chaque ligne d'un brouillon.
 *
 * On n'envoie que des **quantités couvertes** : le serveur recalcule le
 * conditionnement, le minimum de commande, le prix et les totaux. Une
 * modification remplace le brouillon entier plutôt que de le rapiécer — une
 * décision d'achat se lit comme un tout, et un remplacement complet évite
 * qu'un résidu de la version précédente y survive.
 */
function QuantityEditor({ detail }: { detail: PurchasePlanDetail }) {
  const toast = useToast();
  const update = useUpdatePurchasePlan();
  const [draft, setDraft] = useState<Record<string, string>>({});

  const wanted = (line: PurchasePlanLine) => draft[line.id] ?? line.covered_quantity;
  const touched = detail.lines.some((line) => wanted(line) !== line.covered_quantity);

  async function save() {
    try {
      await update.mutateAsync({
        planId: detail.plan.id,
        allocations: detail.lines.map((line) => ({
          rfq_line_id: line.rfq_line_id,
          supplier_quote_line_id: line.supplier_quote_line_id,
          covered_quantity: wanted(line),
        })),
      });
      toast.success("Plan modifié", "Prix, conditionnement et totaux ont été recalculés.");
      setDraft({});
    } catch (error) {
      reportMutationError(toast, "Plan non modifié", error);
    }
  }

  return (
    <div className="space-y-2 border-t border-border/60 px-3 py-3 sm:px-0">
      <p className="text-[12px] text-ink-subtle">
        Ajuster ce qu&apos;on prend sur chaque ligne. Le serveur recalcule le reste — aucun prix
        n&apos;est envoyé d&apos;ici.
      </p>
      <ul className="space-y-2">
        {detail.lines.map((line) => (
          <li key={line.id} className="flex flex-wrap items-center gap-2">
            <label
              className="min-w-0 flex-1 truncate text-[13px] text-ink"
              htmlFor={`covered-${line.id}`}
            >
              {line.description}
            </label>
            <Input
              id={`covered-${line.id}`}
              type="number"
              min="0"
              step="any"
              className="tnum w-32"
              value={wanted(line)}
              onChange={(event) =>
                setDraft((current) => ({ ...current, [line.id]: event.target.value }))
              }
              aria-label={`Quantité retenue — ${line.description}`}
            />
            <span className="text-[12px] text-ink-subtle">
              sur {formatQuantityWithUnit(line.requested_quantity, line.requested_unit)}
            </span>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void save()}
          disabled={!touched || update.isPending}
        >
          {update.isPending ? "Enregistrement…" : "Enregistrer les quantités"}
        </Button>
        {touched && (
          <Button variant="ghost" size="sm" onClick={() => setDraft({})}>
            Revenir
          </Button>
        )}
      </div>
    </div>
  );
}

/** Ce qui est acheté, chez qui, à quel prix figé. Table au bureau, cartes au téléphone. */
function Lines({ detail, editable }: { detail: PurchasePlanDetail; editable: boolean }) {
  return (
    <Card>
      <CardHeader
        title="Ligne par ligne"
        subtitle="Ce que le fournisseur avait annoncé, et le jour où il l'annonçait."
      />
      <CardBody className="p-0 sm:p-4">
        <div className="hidden md:block">
          <TableShell>
            <thead>
              <tr>
                <Th className="max-w-0">Besoin</Th>
                <Th className="max-w-0">Fournisseur</Th>
                <Th align="right" className="whitespace-nowrap">
                  À acheter
                </Th>
                <Th align="right" className="whitespace-nowrap">
                  Prix unitaire
                </Th>
                <Th align="right" className="whitespace-nowrap">
                  Marchandise
                </Th>
                <Th className="hidden @5xl:table-cell">Annoncé</Th>
              </tr>
            </thead>
            <tbody>
              {detail.lines.map((line) => (
                <LineRow key={line.id} line={line} />
              ))}
            </tbody>
          </TableShell>
        </div>

        <ul className="divide-y divide-border/60 md:hidden">
          {detail.lines.map((line) => (
            <li key={line.id}>
              <LineCard line={line} />
            </li>
          ))}
        </ul>

        {editable && <QuantityEditor detail={detail} />}
      </CardBody>
    </Card>
  );
}

function LineWarnings({ line }: { line: PurchasePlanLine }) {
  if (line.warnings.length === 0) return null;
  return (
    <span className="mt-1 flex flex-wrap gap-1">
      {line.warnings.map((warning) => (
        <StatusBadge key={warning} tone={warningTone(warning as ComparisonWarning, false)}>
          {formatComparisonWarning(warning as ComparisonWarning)}
        </StatusBadge>
      ))}
    </span>
  );
}

/** Ce qu'il faut acheter, et le surplus que le conditionnement impose. */
function purchaseLabel(line: PurchasePlanLine): string {
  const bought = formatQuantityWithUnit(line.buy_quantity, line.buy_unit);
  if (Number(line.surplus_quantity) === 0) return bought;
  return `${bought} (dont ${formatQuantityWithUnit(line.surplus_quantity, line.buy_unit)} de surplus)`;
}

function LineRow({ line }: { line: PurchasePlanLine }) {
  return (
    <Tr>
      <Td className="max-w-0">
        <p className="truncate text-[13px] text-ink">{line.description}</p>
        <p className="tnum text-[11px] text-ink-subtle">
          Demandé : {formatQuantityWithUnit(line.requested_quantity, line.requested_unit)}
        </p>
        {line.is_substitute && (
          <StatusBadge tone="info">Substitut ({line.substitute_certainty ?? "certitude inconnue"})</StatusBadge>
        )}
        <LineWarnings line={line} />
      </Td>
      <Td className="max-w-0">
        <p className="truncate text-[13px] text-ink">{line.supplier_name}</p>
        <p className="truncate text-[11px] text-ink-subtle">{formatLeadTime(line.lead_time_days)}</p>
      </Td>
      <Td align="right" className="tnum whitespace-nowrap text-[13px] text-ink">
        {purchaseLabel(line)}
      </Td>
      <Td align="right" className="tnum whitespace-nowrap text-[13px] text-ink-muted">
        {formatAmount(line.unit_price, line.currency)}
      </Td>
      <Td align="right" className="tnum whitespace-nowrap text-[13px] text-ink">
        {formatAmount(line.merchandise_amount, line.currency)}
      </Td>
      <Td className="hidden text-[12px] text-ink-subtle @5xl:table-cell">
        {formatAssertedAvailability(line.available_quantity, line.availability_asserted_on)}
      </Td>
    </Tr>
  );
}

function LineCard({ line }: { line: PurchasePlanLine }) {
  return (
    <div className="px-3 py-3">
      <p className="text-[14px] font-medium text-ink">{line.description}</p>
      <p className="text-[12px] text-ink-muted">{line.supplier_name}</p>
      <dl className="tnum mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[12px]">
        <dt className="text-ink-subtle">Demandé</dt>
        <dd className="text-ink">
          {formatQuantityWithUnit(line.requested_quantity, line.requested_unit)}
        </dd>
        <dt className="text-ink-subtle">À acheter</dt>
        <dd className="text-ink">{purchaseLabel(line)}</dd>
        <dt className="text-ink-subtle">Prix unitaire</dt>
        <dd className="text-ink">{formatAmount(line.unit_price, line.currency)}</dd>
        <dt className="text-ink-subtle">Marchandise</dt>
        <dd className="text-ink">{formatAmount(line.merchandise_amount, line.currency)}</dd>
        <dt className="text-ink-subtle">Délai</dt>
        <dd className="text-ink">{formatLeadTime(line.lead_time_days)}</dd>
      </dl>
      <p className="mt-1 text-[11px] text-ink-subtle">
        {formatAssertedAvailability(line.available_quantity, line.availability_asserted_on)}
      </p>
      <LineWarnings line={line} />
    </div>
  );
}

/**
 * Le transport, **une fois par soumission**.
 *
 * Deux soumissions d'un même fournisseur sont deux commandes et deux
 * transports : c'est pourquoi ce tableau est indexé par soumission et non par
 * fournisseur. L'inverse faisait disparaître un transport sur deux.
 */
function Quotes({ quotes }: { quotes: PurchasePlanQuote[] }) {
  if (quotes.length === 0) return null;

  return (
    <Card>
      <CardHeader
        title="Transport"
        subtitle="Une fois par soumission retenue. Un transport non chiffré n'est pas zéro."
      />
      <CardBody className="p-0 sm:p-4">
        <ul className="divide-y divide-border/60">
          {quotes.map((quote) => (
            <li
              key={quote.supplier_quote_id}
              className="flex flex-wrap items-baseline justify-between gap-2 px-3 py-2 sm:px-0"
            >
              <span className="min-w-0 text-[13px] text-ink">
                <Link
                  to={`/quotes/${quote.supplier_quote_id}`}
                  className="text-info underline underline-offset-2"
                >
                  {quote.supplier_name} — {quote.quote_code}
                </Link>
                {quote.quote_revision > 1 && (
                  <span className="text-ink-subtle"> (rév. {quote.quote_revision})</span>
                )}
              </span>
              <span className="tnum text-[13px] text-ink">
                {quote.freight_amount === null
                  ? "Non chiffré"
                  : formatAmount(quote.freight_amount, quote.currency)}
              </span>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}

/**
 * L'historique des révisions du même plan.
 *
 * Le code est stable, la révision change : c'est la même décision qui évolue,
 * et chacune de ses versions reste lisible telle qu'elle a été signée.
 */
function Revisions({ detail }: { detail: PurchasePlanDetail }) {
  if (detail.revisions.length <= 1) return null;

  return (
    <Card>
      <CardHeader
        title="Révisions"
        subtitle="La même décision, dans ses versions successives. Aucune n'est réécrite."
      />
      <CardBody className="p-0 sm:p-4">
        <ul className="divide-y divide-border/60">
          {detail.revisions.map((item) => (
            <li
              key={item.id}
              className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 sm:px-0"
            >
              <Link
                to={`/purchase-plans/${item.id}`}
                className={
                  item.id === detail.plan.id
                    ? "text-[13px] font-medium text-ink"
                    : "text-[13px] text-info underline underline-offset-2"
                }
              >
                {formatPlanCode(item)}
              </Link>
              <span className="flex items-center gap-2 text-[12px] text-ink-subtle">
                {item.approved_at ? `${item.approved_by} · ${formatDate(item.approved_at)}` : ""}
                <StatusBadge tone={planStatusTone(item.status)}>
                  {formatPlanStatus(item.status)}
                </StatusBadge>
              </span>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}
